/**
 * GLSL to compute an additional fog factor based on x/y distance from the scene center.
 * @module
 */
import * as THREE from "three";
import type { DesignMaterialShaderChunks } from "../designMaterialShaderChunks";
import type { ShaderProgramLike } from "../shaderUtils";
import { ensureWorldPositionInFragShader } from "./ensureWorldPositionInFragShader";

// This is for testing: we can automatically determine during three.js upgrades whether the fog shader has changed.
const EXPECTED_ORIGINAL_FOG_SHADER = `
#ifdef USE_FOG

	#ifdef FOG_EXP2

		float fogFactor = 1.0 - exp( - fogDensity * fogDensity * vFogDepth * vFogDepth );

	#else

		float fogFactor = smoothstep( fogNear, fogFar, vFogDepth );

	#endif

	gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fogFactor );

#endif
`;

const fogWithRadialFog = `
#ifdef USE_FOG

  #ifdef FOG_EXP2

    float fogFactor = 1.0 - exp( - fogDensity * fogDensity * vFogDepth * vFogDepth );

  #else

    float fogFactor = smoothstep( fogNear, fogFar, vFogDepth );

  #endif

  fogFactor += smoothstep( radialFogParams.z, radialFogParams.w, distance(vWorldPosition.xy, radialFogParams.xy));

  gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, clamp(fogFactor, 0.0, 1.0) );

#endif
`;

export function includeRadialFog(
  designMaterialShaderChunks: DesignMaterialShaderChunks,
  shader: ShaderProgramLike,
): void {
  if (!(THREE.ShaderChunk["fog_fragment"].replace(/\s/g, "") === EXPECTED_ORIGINAL_FOG_SHADER.replace(/\s/g, ""))) {
    console.assert(false);
  }
  ensureWorldPositionInFragShader(shader);
  designMaterialShaderChunks.fog = fogWithRadialFog;

  // encodes the xy center and the near/far values.
  designMaterialShaderChunks.fragmentDefinitions.push("uniform vec4 radialFogParams;");
}
